import { LOADING_STATES } from 'src/utils/maps';
import { trackEvent } from 'src/services/analytics';
import { isDailyBoostTreatment } from 'src/utils/dailyBoostVariant';
import { DAILY_BOOST_CONSTANTS } from '../constants/daily-boost';
import { getDailyBoost, postDailyBoostRepair } from '../api/daily-boost';

export const setDailyBoostData = (payload) => ({
  type: DAILY_BOOST_CONSTANTS.setStateData,
  payload,
});

export const setDailyBoostStatus = (payload) => ({
  type: DAILY_BOOST_CONSTANTS.setStateStatus,
  payload,
});

export const setDailyBoostError = (payload) => ({
  type: DAILY_BOOST_CONSTANTS.setStateError,
  payload,
});

export const closeDailyBoostSpin = () => ({
  type: DAILY_BOOST_CONSTANTS.closeSpin,
});

// The ladder (data.ladder) is server-computed and nothing client-side
// rebuilds it after a boost — without a refetch the card keeps showing
// pre-boost nodes until a page reload (found on the very first prod
// boost: the day-1 intro opened over a card still saying "start your
// streak"). The refresh waits out the ~3s landing celebration so the
// data swap never re-renders nodes mid-ripple, and the celebration
// timers hold their own captured copies of the optimistic fields.
const refreshDailyBoostAfterCelebration = (dispatch) => {
  setTimeout(() => dispatch(fetchDailyBoost()), 3200);
};

// Thunk: closing any boost overlay (wheel/gift milestone AND the day-1
// intro — everything except the on-demand "?" help view) schedules the
// post-celebration refresh. It must not run earlier: the deferred
// landing reads lastBoostTotal/lastDailyReward at close, and a refetch
// while the modal is still up would wipe them.
export const closeDailyBoostMilestone = () => (dispatch, getState) => {
  const milestone = getState().dailyBoost && getState().dailyBoost.milestone;
  const wasBoostOverlay = Boolean(milestone && milestone.data && !milestone.data.help);

  dispatch({ type: DAILY_BOOST_CONSTANTS.closeMilestone });

  if (wasBoostOverlay) {
    refreshDailyBoostAfterCelebration(dispatch);
  }
};

/**
 * Open the streak explainer. Bare call = the "?" help variant. With
 * { celebrate: true, petName } it renders the celebratory day-1 intro
 * ("{pet}'s streak has started!") — used when the first boost happened
 * somewhere with no streak UI (the /start funnel's vote) and the pet
 * page redeems the pending explanation on first visit.
 */
export const openDailyBoostIntro = (payload = null) => ({
  type: DAILY_BOOST_CONSTANTS.showIntro,
  payload,
});

export const resetDailyBoost = () => ({
  type: DAILY_BOOST_CONSTANTS.reset,
});

/**
 * Fetch the streak state. No-op for control users — the backend would
 * answer {eligible:false} anyway, but skipping the call keeps control
 * page-loads free of any Daily Boost traffic.
 *
 * Concurrent callers share ONE request: the desktop and mobile header
 * chips mount together and the detail card can join them, which used
 * to fire the expensive endpoint 2-3x per page load (Codex review).
 * Sequential refetches (after a boost/restore) are unaffected.
 */
let inFlight = null;

export const fetchDailyBoost = () => (dispatch) => {
  if (!isDailyBoostTreatment()) return Promise.resolve();
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      dispatch(setDailyBoostStatus(LOADING_STATES.LOADING));
      const response = await getDailyBoost();

      if (response.status !== 200) {
        dispatch(setDailyBoostError(`status ${response.status}`));
        return;
      }

      const json = await response.json();
      dispatch(setDailyBoostData(json));
    } catch (e) {
      dispatch(setDailyBoostError(e.message));
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};

/**
 * Apply the dailyBoost block from a free-vote response: advances the
 * pill optimistically and queues the spin reveal + milestone modal.
 */
export const applyDailyBoostResult = (result) => (dispatch) => {
  if (!result) return;

  dispatch({ type: DAILY_BOOST_CONSTANTS.applyBoostResult, payload: result });

  // Plain days with NO overlay at all: schedule the post-celebration
  // refresh here. Every overlay day (wheel, gift milestone, day-1
  // intro) refreshes via closeDailyBoostMilestone instead — the
  // deferred landing reads the optimistic reward fields at close, so a
  // refetch while the modal is up would wipe them.
  const opensOverlay = result.spin || result.milestoneUnlocked || result.currentDays === 1;
  if (!result.alreadyBoostedToday && !opensOverlay) {
    refreshDailyBoostAfterCelebration(dispatch);
  }

  if (!result.alreadyBoostedToday) {
    trackEvent('daily_boost_claimed', {
      streak_day: result.currentDays,
      badge_tier: result.badgeTier,
    });
    if (result.spin && result.spin.spottedItem) {
      trackEvent('streak_spin_result', {
        streak_day: result.currentDays,
        votes: result.spin.spottedItem.votes,
      });
    }
    if (result.milestoneUnlocked) {
      trackEvent('streak_milestone_reached', {
        day: result.milestoneUnlocked.day,
      });
    }
  }
};

/**
 * Spend an available freeze to repair a recently broken streak (48h grace).
 */
export const repairDailyBoost = () => async (dispatch) => {
  try {
    const response = await postDailyBoostRepair();

    if (response.status !== 200) {
      return false;
    }

    const json = await response.json();
    dispatch(setDailyBoostData(json));
    trackEvent('streak_repair_used', { streak_day: json.currentDays });
    return true;
  } catch (e) {
    return false;
  }
};
